import type { TrackingType } from "@patient-watch/types";
import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { templatesCopyEn } from "@patient-watch/i18n/webapp/en-GB/templatesCopy";
import { templatesCopyDe } from "@patient-watch/i18n/webapp/de-DE/templatesCopy";
import { templatesCopyDa } from "@patient-watch/i18n/webapp/da-DK/templatesCopy";
import { templatesCopyEnUs } from "@patient-watch/i18n/webapp/en-US/templatesCopy";
import { templatesCopyFr } from "@patient-watch/i18n/webapp/fr-FR/templatesCopy";
import { templatesCopyIt } from "@patient-watch/i18n/webapp/it-IT/templatesCopy";
import { templatesCopyEs } from "@patient-watch/i18n/webapp/es-ES/templatesCopy";

interface TemplatesCopy {
    templates: string;
    navDescription: string;
    myTemplatesTitle: string;
    myTemplatesDescription: string;
    myTemplatesAria: string;
    publicTemplatesTitle: string;
    publicTemplatesDescription: string;
    /** Breadcrumb label for the templates root, e.g. "New" / "Neu". */
    breadcrumbNew: string;
    /** CTA on the public templates page before any template is selected. */
    selectAtLeastOneTemplate: string;
    /** CTA once one or more public templates are selected. */
    addTemplatesToAccount: (count: number) => string;
    creatingTemplates: string;
    browsePublic: string;
    browsePublicAria: string;
    newTemplate: string;
    newTemplateAria: string;
    makeMyOwn: string;
    favourites: string;
    noQuestionnaires: string;
    moreQuestionnaires: (count: number) => string;
    useTemplate: string;
    newDiary: string;
    searchPlaceholder: string;
    searchAria: string;
    clearSearchAria: string;
    filters: string;
    clearAll: string;
    procedureFilterPrefix: string;
    statusFilterPrefix: string;
    substanceFilterPrefix: string;
    subsiteFilterPrefix: string;
    siteFilterPrefix: string;
    typeFilterPrefix: string;
    areaFilterPrefix: string;
    consultationFilterPrefix: string;
    removeProcedureFilterAria: (label: string) => string;
    removeStatusFilterAria: (label: string) => string;
    removeOwnerFilterAria: (name: string) => string;
    removePeopleFilterAria: (name: string) => string;
    removeSubstanceFilterAria: string;
    removeSubsiteFilterAria: string;
    removeSiteFilterAria: string;
    removeShockwaveTypeFilterAria: string;
    removeAreaFilterAria: string;
    removeConsultationFilterAria: string;
    ownerFilterLabel: string;
    ownerFilterPlaceholder: string;
    statusFilterLabel: string;
    statusFilterPlaceholder: string;
    peopleOnTemplateLabel: string;
    peopleOnTemplatePlaceholder: string;
    procedureTypeLabel: string;
    procedureTypePlaceholder: string;
    areaLabel: string;
    substanceLabel: string;
    subsiteLabel: string;
    typeLabel: string;
    siteLabel: string;
    consultationLabel: string;
    selectPlaceholder: string;
    clearFilterAria: (label: string) => string;
    injection: string;
    shockwave: string;
    surgery: string;
    consultation: string;
    medication: string;
    product: string;
    published: string;
    draft: string;
    noTemplatesYet: string;
    noMatchingTemplates: string;
    noTemplatesYetHint: string;
    noMatchingHint: string;
    createFirstTemplate: string;
    emptyTemplatesHelpLink: string;
    tableHint: string;
    forms: string;
    lastEdited: string;
    procedure: string;
    area: string;
    people: string;
    details: string;
    actions: string;
    select: string;
    useTemplateAria: string;
    editTemplateAria: string;
    tapToUseTemplate: string;
    useThisTemplateAria: string;
    clickRowToUseAria: string;
    publicTemplatesTableAria: string;
    yourTemplatesTableAria: string;
    noTemplatesInView: string;
    showingTemplatesRange: (start: number, end: number, total: number) => string;
    previous: string;
    next: string;
    save: string;
    rename: string;
    editTemplate: string;
    addToFavourites: string;
    removeFromFavourites: string;
    copyUrl: string;
    shareTemplate: string;
    labelsAndDiaryType: string;
    publishTemplate: string;
    linkToMasterTemplate: string;
    duplicate: string;
    delete: string;
    deleteTemplateTitle: string;
    deleteTemplateDescription: string;
    cancel: string;
    templateUpdatedToast: string;
    copiedToClipboardToast: string;
    unnamedTemplate: string;
    userFallback: (idPrefix: string) => string;
    eventTypeLabel: (type: TrackingType) => string;
    statusLabel: (status: "published" | "draft") => string;
    questionnaireSchedule: string;
    interventionStep: string;
    injectionDetails: string;
    surgeryClinicians: string;
    surgeryDetails: string;
    shockwaveClinicians: string;
    shockwaveDetails: string;
    /** "No intervention" radio option on the intervention step, e.g. "No intervention" / "Keine Behandlung". */
    noIntervention: string;
    /** The template step that chooses which date follow-ups count from. Stepper label, e.g. "Date". */
    dateStep: string;
    dateQuestion: string;
    /** Option: the procedure date is the diary start date. */
    dateDiaryStart: string;
    dateDiaryStartHint: string;
    /** Option: the procedure date is left blank for the patient to give. */
    dateAskPatient: string;
    dateAskPatientHint: string;
    /** Primary action on the final template-creation step, e.g. "Save template" / "Vorlage speichern". */
    saveTemplate: string;
    /** Lock icon button labels: "Lock" / "Unlock" (aria + tooltip) and the "Locked" table column header. */
    lockLabel: string;
    unlockLabel: string;
    lockedLabel: string;
    /** Shown when a recipient cannot edit a locked section. */
    lockedByTemplateCreator: string;
    /** Placeholder for the optional product usage-instructions textarea. */
    usageInstructionsPlaceholder: string;
    /** Validation message when a new product is added without a name. */
    productNameRequired: string;
    /** Label for the contact picker, e.g. "Contact email". */
    contactEmail: string;
    /** Placeholder for the contact-email picker, e.g. "Select a contact email". */
    selectContactPlaceholder: string;
    /** Shown on a contact option that is the default for one or more organisations. */
    defaultContactForOrganisation: (organisationNames: string) => string;
    templateCreatedTitle: string;
    templateCreatedMessage: string;
    templateNotFoundTitle: string;
    templateNotFoundDescription: string;
    backToTemplates: string;
}

const templatesCopy: Record<SupportedLocale, TemplatesCopy> = {
    "en-GB": templatesCopyEn,
    "en-US": templatesCopyEnUs,
    "de-DE": templatesCopyDe,
    "da-DK": templatesCopyDa,
    "fr-FR": templatesCopyFr,
    "it-IT": templatesCopyIt,
    "es-ES": templatesCopyEs,
};

export function useTemplatesCopy(): TemplatesCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(templatesCopy, locale);
}

export function getTemplatesCopy(locale: SupportedLocale): TemplatesCopy {
    return pickAppCopy(templatesCopy, locale);
}

export function getEventTypeFilterOptions(copy: TemplatesCopy): { label: string; value: TrackingType }[] {
    return [
        { label: copy.injection, value: "injection" },
        { label: copy.shockwave, value: "shockwave" },
        { label: copy.surgery, value: "surgery" },
        { label: copy.consultation, value: "education" },
    ];
}

export function getStatusFilterOptions(copy: TemplatesCopy): { label: string; value: "published" | "draft" }[] {
    return [
        { label: copy.published, value: "published" },
        { label: copy.draft, value: "draft" },
    ];
}

export function templateStepperLabel(key: string, copy: TemplatesCopy): string {
    const map: Record<string, string> = {
        trackers: copy.questionnaireSchedule,
        events: copy.interventionStep,
        date: copy.dateStep,
        injection: copy.injection,
        "injection-details": copy.injectionDetails,
        surgery: copy.surgeryClinicians,
        "surgery-details": copy.surgeryDetails,
        shockwave: copy.shockwaveClinicians,
        "shockwave-details": copy.shockwaveDetails,
        product: copy.product,
        education: copy.consultation,
        // Admin-only steps, in English like the rest of the admin view.
        screener: "Screener",
        consent: "Consent",
        "hospital-number": "Hospital number",
        labels: "Labels",
    };
    return map[key] ?? copy.eventTypeLabel(key as TrackingType);
}
